import { QueryClient, QueryFunction } from "@tanstack/react-query";

/**
 * Whether a 403 is an organisation-level suspension.
 *
 * Reads a CLONE of the response: the caller still needs the original body for
 * its own error handling, and a Response body can only be consumed once.
 * Any parse failure means "not a suspension" — a malformed body must not
 * bounce the user out of the app.
 */
async function accountHoldDestination(res: Response): Promise<string | null> {
  try {
    const body = await res.clone().json();
    // ACCOUNT_CLOSED (archived org) gets the same treatment — the account
    // is denied and the suspended screen's "contact support" is the right
    // destination. ACCOUNT_LOCKED deliberately not: locked is read-only and
    // the app stays usable for viewing.
    if (body?.code === 'ACCOUNT_SUSPENDED' || body?.code === 'ACCOUNT_CLOSED') return '/account-suspended';
    // ⚠ Pending review and rejection used to fall through to a raw error.
    // App.tsx shows the pending page only when the org context has LOADED with
    // activationStatus pending/rejected; when that context failed to load, a
    // held account got the full app shell with every request erroring. The
    // server already says exactly what is wrong — take them to the page for it.
    if (body?.code === 'ACCOUNT_PENDING_REVIEW' || body?.code === 'ACCOUNT_REJECTED') return '/pending-approval';
    return null;
  } catch {
    return null;
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const contentType = res.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const errorData = await res.json();
      // Create a structured error with the response data
      const error = new Error(`${res.status}: ${errorData.message || res.statusText}`) as any;
      error.status = res.status;
      error.errorCode = errorData.errorCode;
      error.data = errorData;
      throw error;
    } else {
      const text = (await res.text()) || res.statusText;
      throw new Error(`${res.status}: ${text}`);
    }
  }
}

// Token refresh function
async function refreshToken(): Promise<string | null> {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;

    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      localStorage.setItem('token', data.token);
      localStorage.setItem('token_timestamp', Date.now().toString());
      return data.token;
    }
    
    return null;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return null;
  }
}

// Enhanced request function with automatic token refresh
async function requestWithAuth(
  method: string,
  url: string,
  data?: unknown,
  isRetry = false,
  options?: ApiRequestOptions
): Promise<any> {
  const headers: Record<string, string> = {};

  // Add signup URL for tracking where user started their signup journey
  const signupUrl = localStorage.getItem('signup_url') || window.location.pathname;
  if (signupUrl) {
    headers["X-Signup-Url"] = signupUrl;
  }

  if (data) {
    headers["Content-Type"] = "application/json";
  }

  // Attach X-Idempotency-Key header if provided (non-empty trimmed string).
  // The server trims and ignores empty values — mirror that here so a blank
  // key never produces a header. See contract test:
  // server/lib/__tests__/reactivateHandler.test.ts
  if (options?.idempotencyKey && options.idempotencyKey.trim().length > 0) {
    headers["X-Idempotency-Key"] = options.idempotencyKey.trim();
  }
  
  // For registration, login, password reset, email validation, and activation, don't require token
  if (url.includes('/api/auth/register') || 
      url.includes('/api/auth/login') || 
      url.includes('/api/auth/forgot-password') ||
      url.includes('/api/auth/reset-password') ||
      url.includes('/api/auth/validate-email') ||
      // Passwordless signup's OTP step runs before any session exists — verify
      // is what issues the first token. Requiring one here threw "No
      // authentication token available" and bounced new users to /auth/login.
      url.includes('/api/auth/signup/verify') ||
      url.includes('/api/auth/signup/resend') ||
      // Only the pre-session activation calls are public. Everything else under
      // /api/activation (provision, status, qualification, intake, call) needs
      // the session: sent without a token, GET /api/activation/qualification
      // 401'd right after the onboarding number step, the 401 retry went out
      // tokenless too, and the session was wiped — straight back to signup.
      url.includes('/api/activation/register') ||
      url.includes('/api/activation/verify') ||
      url.includes('/api/activation/resend-otp')) {
  } else {
    // Use only JWT tokens from localStorage (no static fallback)
    const token = localStorage.getItem('token');
    if (!token) {
      // On an auth page there is nowhere to redirect to: a global query firing
      // here used to reload /auth/login with ?return=/auth/login?... nested one
      // level deeper each time, forever.
      if (!window.location.pathname.startsWith('/auth/')) {
        console.error('❌ [API] No token available for protected endpoint');
        // No token available - redirect to login, preserving current page as return destination
        const returnPath = window.location.pathname + window.location.search;
        window.location.href = `/auth/login?return=${encodeURIComponent(returnPath)}`;
      }
      throw new Error('No authentication token available');
    }
    headers["Authorization"] = `Bearer ${token}`;
  }

  try {
    const res = await fetch(url, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });

    // If we get a 401 and haven't retried yet, try to refresh the token
    if (res.status === 401 && !isRetry && !url.includes('/api/auth/')) {
      const newToken = await refreshToken();
      if (newToken) {
        // Retry the request with the new token. Pass `options` through so
        // the X-Idempotency-Key header (if any) is preserved across refresh.
        return requestWithAuth(method, url, data, true, options);
      } else {
        // Token refresh failed - redirect to login, preserving current page as return destination
        localStorage.removeItem('token');
        localStorage.removeItem('token_timestamp');
        const returnPath = window.location.pathname + window.location.search;
        window.location.href = `/auth/login?return=${encodeURIComponent(returnPath)}`;
        return;
      }
    }

    // A suspended organisation gets one clear screen instead of a page full of
    // failed requests. The server allowlists billing and auth, so the suspension
    // page can still offer a way to settle up — do not redirect those away.
    if (res.status === 403) {
      const destination = await accountHoldDestination(res);
      if (destination && !window.location.pathname.startsWith(destination)) {
        window.location.href = destination;
        return;
      }
    }

    // If the retry also returned 401, the session is unrecoverable — redirect to login
    if (res.status === 401 && isRetry) {
      localStorage.removeItem('token');
      localStorage.removeItem('token_timestamp');
      const returnPath = window.location.pathname + window.location.search;
      window.location.href = `/auth/login?return=${encodeURIComponent(returnPath)}`;
      return;
    }

    await throwIfResNotOk(res);
    
    // Return JSON for successful responses
    const contentType = res.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return await res.json();
    }
    
    return res;
  } catch (error) {
    console.error('❌ [API] Request failed:', error);
    throw error;
}
}

export interface ApiRequestOptions {
  /**
   * Optional per-attempt idempotency nonce. When provided (non-empty,
   * trimmed), the fetch is sent with an `X-Idempotency-Key` header. The
   * server composes this into the Stripe idempotency key used for
   * reactivate/upgrade POSTs. Contract covered by the server handler test
   * at server/lib/__tests__/reactivateHandler.test.ts.
   */
  idempotencyKey?: string;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: ApiRequestOptions,
): Promise<any> {
  return requestWithAuth(method, url, data, false, options);
}

// Admin-specific API request function
export async function adminRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<any> {
  const headers: Record<string, string> = {};
  
  if (data) {
    headers["Content-Type"] = "application/json";
  }
  
  // Add admin authorization header (using adminToken for consistency)
  const adminToken = localStorage.getItem('adminToken');
  if (adminToken) {
    headers["Authorization"] = `Bearer ${adminToken}`;
  }

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  // Handle 401 by redirecting to admin login
  if (res.status === 401) {
    localStorage.removeItem('adminToken');
    window.location.href = '/admin';
    throw new Error('Unauthorized');
  }

  await throwIfResNotOk(res);
  
  // Return JSON for successful responses
  const contentType = res.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return await res.json();
  }
  
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Construct URL from query key segments
    let url = queryKey[0] as string;
    if (queryKey.length > 1) {
      // Check if second element is a query string (starts with a letter or contains =)
      const secondElement = queryKey[1] as string;
      if (typeof secondElement === 'string' && (secondElement.includes('=') || secondElement.match(/^[a-z]/i))) {
        // It's query parameters, append with ?
        url += `?${secondElement}`;
      } else {
        // Primitives are path segments. A plain object is query parameters —
        // it used to be stringified into the path, so the dialer's
        // ['/api/call-logs/recent', { limit: 5 }] requested
        // /api/call-logs/recent/[object Object] and 404'd on every load.
        const params = new URLSearchParams();
        for (let i = 1; i < queryKey.length; i++) {
          const part = queryKey[i];
          if (part == null) continue;
          if (typeof part === 'object' && !Array.isArray(part)) {
            for (const [k, v] of Object.entries(part as Record<string, unknown>)) {
              if (v != null) params.append(k, String(v));
            }
          } else {
            url += `/${part}`;
          }
        }
        const qs = params.toString();
        if (qs) url += `${url.includes('?') ? '&' : '?'}${qs}`;
      }
    }

    try {
      return await requestWithAuth('GET', url);
    } catch (error: any) {
      if (error.message.includes('401') && unauthorizedBehavior === "returnNull") {
        return null;
      }
      throw error;
    }
  };

// Tiered staleTime constants (in milliseconds)
export const STALE_TIME = {
  // Real-time data - refetch on window focus
  REAL_TIME: 0,
  // Short-lived data (1 minute) - UI states, counters
  SHORT: 1 * 60 * 1000,
  // User/org identity (5 minutes) - user profile, capabilities, org settings
  IDENTITY: 5 * 60 * 1000,
  // Reference data (1 hour) - phone numbers, templates, teams
  REFERENCE: 60 * 60 * 1000,
  // Static data (12 hours) - pricing plans, help docs, compliance rules
  STATIC: 12 * 60 * 60 * 1000,
  // Never refetch - explicitly managed cache
  INFINITY: Infinity,
} as const;

// Network error detection helper
function isNetworkError(error: any): boolean {
  return (
    error.message?.includes('fetch') ||
    error.message?.includes('network') ||
    error.message?.includes('Failed to fetch') ||
    error.name === 'NetworkError' ||
    error.name === 'TypeError'
  );
}

// Server error detection (5xx)
function isServerError(error: any): boolean {
  const status = error.status || parseInt(error.message?.match(/^(\d{3}):/)?.[1] || '0');
  return status >= 500 && status < 600;
}

// Retry logic for queries
function shouldRetryQuery(failureCount: number, error: any): boolean {
  // Don't retry auth errors or client errors (4xx except 408, 429)
  const status = error.status || parseInt(error.message?.match(/^(\d{3}):/)?.[1] || '0');
  if (status === 401 || status === 403 || status === 404) {
    return false;
  }

  // Retry network errors up to 3 times (failureCount: 1, 2, 3)
  if (isNetworkError(error)) {
    return failureCount <= 3;
  }

  // Retry server errors (5xx) up to 2 times (failureCount: 1, 2)
  if (isServerError(error)) {
    return failureCount <= 2;
  }

  // Retry rate limits (429) once (failureCount: 1)
  if (status === 429) {
    return failureCount <= 1;
  }

  // Retry timeouts (408) up to 2 times (failureCount: 1, 2)
  if (status === 408) {
    return failureCount <= 2;
  }

  // Don't retry other errors
  return false;
}

// Exponential backoff with jitter for retries
function getRetryDelay(attemptIndex: number): number {
  // Base delay: 1s, 2s, 4s
  const exponentialDelay = Math.min(1000 * Math.pow(2, attemptIndex), 10000);
  // Add jitter (±20%) to prevent thundering herd
  const jitter = exponentialDelay * 0.2 * (Math.random() * 2 - 1);
  return exponentialDelay + jitter;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      // Smart refetching disabled by default - components opt-in with refetchInterval
      refetchInterval: false,
      // Default 5 min staleTime for better performance (instead of Infinity)
      // Balances freshness with responsiveness - data feels instant but stays reasonably fresh
      staleTime: STALE_TIME.IDENTITY, // 5 minutes
      // Enable background refetch on window focus for better UX (Microsoft Teams pattern)
      // Users expect fresh data when returning to the app
      refetchOnWindowFocus: true,
      // Smart retry with exponential backoff and error classification
      retry: shouldRetryQuery,
      retryDelay: getRetryDelay,
      // Keep data in cache longer for instant navigation (Dropbox pattern)
      gcTime: 30 * 60 * 1000, // 30 minutes (renamed from cacheTime in v5)
    },
    mutations: {
      // Retry critical mutations (network/server errors only)
      retry: (failureCount, error: any) => {
        // Never retry auth errors
        const status = error.status || parseInt(error.message?.match(/^(\d{3}):/)?.[1] || '0');
        if (status === 401 || status === 403) {
          return false;
        }
        // Retry network errors once (failureCount: 1)
        if (isNetworkError(error) && failureCount <= 1) {
          return true;
        }
        // Retry server errors once (failureCount: 1)
        if (isServerError(error) && failureCount <= 1) {
          return true;
        }
        return false;
      },
      retryDelay: getRetryDelay,
    },
  },
});
